// Dove si cammina in PopTown (artboard 56): piazza, anello di ciottoli e vialetti, tracciati a
// mano sulla città vuota (1672×941) e ricontrollati ridipingendoli sopra l'immagine. Aiuole,
// alberi e lampioni dentro quelle zone sono ostacoli. La griglia si ricava all'avvio dai poligoni,
// con un margine dai bordi, e su di lei si cercano i percorsi (A* a otto direzioni).

export const WORLD = { w: 1672, h: 941 };
export const CELL = 8;

type Poly = [number, number][];

const WALK: Poly[] = [
  [
    [130, 395],
    [340, 392],
    [470, 330],
    [535, 302],
    [600, 262],
    [820, 255],
    [1000, 262],
    [1100, 285],
    [1200, 320],
    [1330, 345],
    [1335, 385],
    [1300, 470],
    [1295, 560],
    [1240, 560],
    [1185, 620],
    [1180, 700],
    [1250, 705],
    [1420, 745],
    [1560, 780],
    [1660, 800],
    [1660, 860],
    [1420, 820],
    [1290, 790],
    [1240, 770],
    [1120, 770],
    [1040, 790],
    [940, 815],
    [900, 830],
    [890, 941],
    [760, 941],
    [760, 830],
    [640, 800],
    [560, 770],
    [480, 745],
    [420, 760],
    [420, 820],
    [290, 835],
    [185, 800],
    [0, 770],
    [0, 715],
    [110, 712],
    [250, 700],
    [300, 675],
    [220, 640],
    [190, 620],
    [150, 560],
    [140, 500],
    [130, 450],
  ],
];

const BLOCK: Poly[] = [
  [
    [265, 600],
    [330, 560],
    [420, 545],
    [455, 575],
    [440, 630],
    [320, 645],
    [265, 625],
  ],
  [
    [1095, 700],
    [1150, 670],
    [1215, 690],
    [1215, 760],
    [1110, 770],
  ],
  [
    [420, 700],
    [470, 670],
    [520, 700],
    [520, 800],
    [430, 800],
  ],
  [
    [1272, 775],
    [1308, 775],
    [1308, 800],
    [1272, 800],
  ],
];

export interface Grid {
  cols: number;
  rows: number;
  /** 1 = si cammina (già ristretto del margine) */
  open: Uint8Array;
}

function inside(poly: Poly, x: number, y: number): boolean {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** La griglia camminabile, con `margin` celle di distanza dai bordi e dagli ostacoli. */
export function buildGrid(margin = 2): Grid {
  const cols = Math.ceil(WORLD.w / CELL);
  const rows = Math.ceil(WORLD.h / CELL);
  const raw = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const x = c * CELL + CELL / 2;
      const y = r * CELL + CELL / 2;
      raw[r * cols + c] =
        WALK.some((p) => inside(p, x, y)) && !BLOCK.some((p) => inside(p, x, y)) ? 1 : 0;
    }
  const open = new Uint8Array(raw);
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      if (!raw[r * cols + c]) continue;
      search: for (let dr = -margin; dr <= margin; dr++)
        for (let dc = -margin; dc <= margin; dc++) {
          const rr = r + dr;
          const cc = c + dc;
          if (rr < 0 || cc < 0 || rr >= rows || cc >= cols || !raw[rr * cols + cc]) {
            // i bordi dell'immagine non chiudono i vialetti che escono dalla scena
            if (rr >= 0 && cc >= 0 && rr < rows && cc < cols) {
              open[r * cols + c] = 0;
              break search;
            }
          }
        }
    }
  return { cols, rows, open };
}

export function walkable(g: Grid, x: number, y: number): boolean {
  const c = Math.floor(x / CELL);
  const r = Math.floor(y / CELL);
  return c >= 0 && r >= 0 && c < g.cols && r < g.rows && g.open[r * g.cols + c] === 1;
}

/** Un punto camminabile a caso, facoltativamente dentro un rettangolo e fuori da un altro. */
export function randomSpot(
  g: Grid,
  within?: { x0: number; y0: number; x1: number; y1: number },
  avoid?: { x0: number; y0: number; x1: number; y1: number },
): { x: number; y: number } {
  for (let i = 0; i < 400; i++) {
    const x = within
      ? within.x0 + Math.random() * (within.x1 - within.x0)
      : Math.random() * WORLD.w;
    const y = within
      ? within.y0 + Math.random() * (within.y1 - within.y0)
      : Math.random() * WORLD.h;
    if (!walkable(g, x, y)) continue;
    if (avoid && x > avoid.x0 && x < avoid.x1 && y > avoid.y0 && y < avoid.y1) continue;
    return { x, y };
  }
  return { x: 820, y: 520 };
}

/** Linea libera fra due punti: per accorciare i percorsi trovati sulla griglia. */
export function clearLine(g: Grid, ax: number, ay: number, bx: number, by: number): boolean {
  const steps = Math.ceil(Math.hypot(bx - ax, by - ay) / (CELL / 2));
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    if (!walkable(g, ax + (bx - ax) * t, ay + (by - ay) * t)) return false;
  }
  return true;
}

/** Il percorso da A a B fra gli ostacoli, già accorciato; vuoto se B non si raggiunge. */
export function findPath(
  g: Grid,
  from: { x: number; y: number },
  to: { x: number; y: number },
): { x: number; y: number }[] {
  const idx = (x: number, y: number) =>
    Math.min(g.rows - 1, Math.max(0, Math.floor(y / CELL))) * g.cols +
    Math.min(g.cols - 1, Math.max(0, Math.floor(x / CELL)));
  const start = idx(from.x, from.y);
  const goal = idx(to.x, to.y);
  if (!g.open[goal]) return [];
  const gScore = new Float32Array(g.cols * g.rows).fill(Infinity);
  const came = new Int32Array(g.cols * g.rows).fill(-1);
  const open: number[] = [start];
  const f = new Float32Array(g.cols * g.rows).fill(Infinity);
  const h = (i: number) =>
    Math.hypot((i % g.cols) - (goal % g.cols), Math.floor(i / g.cols) - Math.floor(goal / g.cols));
  gScore[start] = 0;
  f[start] = h(start);
  const closed = new Uint8Array(g.cols * g.rows);
  while (open.length) {
    let best = 0;
    for (let k = 1; k < open.length; k++) if (f[open[k]] < f[open[best]]) best = k;
    const cur = open.splice(best, 1)[0];
    if (cur === goal) break;
    closed[cur] = 1;
    const cx = cur % g.cols;
    const cy = Math.floor(cur / g.cols);
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        if (!dx && !dy) continue;
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= g.cols || ny >= g.rows) continue;
        const n = ny * g.cols + nx;
        if (!g.open[n] || closed[n]) continue;
        const cost = gScore[cur] + (dx && dy ? 1.414 : 1);
        if (cost < gScore[n]) {
          if (gScore[n] === Infinity) open.push(n);
          came[n] = cur;
          gScore[n] = cost;
          f[n] = cost + h(n);
        }
      }
  }
  if (came[goal] === -1 && goal !== start) return [];
  const cells: { x: number; y: number }[] = [];
  for (let c = goal; c !== -1 && c !== start; c = came[c])
    cells.unshift({
      x: (c % g.cols) * CELL + CELL / 2,
      y: Math.floor(c / g.cols) * CELL + CELL / 2,
    });
  cells.push({ x: to.x, y: to.y });
  // accorciato: si tiene un punto solo se dal precedente non si vede il successivo
  const out: { x: number; y: number }[] = [];
  let anchor = from;
  for (let i = 0; i < cells.length; i++) {
    const next = cells[i + 1];
    if (!next || !clearLine(g, anchor.x, anchor.y, next.x, next.y)) {
      out.push(cells[i]);
      anchor = cells[i];
    }
  }
  return out;
}
